import React from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitleCommon, {DialogCloseCommon} from "./DialogTitleCommon";
import DialogContent from "@mui/material/DialogContent";
import {
  Alerts,
  AxiosErrorCode,
  CPSMessages,
  HttpStatusCode,
  PageNames,
} from "commonUtils/enumerations";
import {dashBoardMessageStyle} from "features/Dashboard/components/DashboardMessages/dashBoardMesssageStyle";
import type {MessageListDialogProps} from "features/Dashboard/components/DashboardMessages/dashBoardMesssageStyle";
import Box from "@mui/material/Box";
import FormValidationAlert from "components/FormComponents/Alert/FormValidationAlert";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import type {SelectChangeEvent} from "@mui/material/Select";
import {firstFiveYears, isValidObject, loggedMessage} from "commonUtils/utils";
import MenuItem from "@mui/material/MenuItem";
import {postRequest, setHeader} from "repository/genericRepository";
import {store} from "../../redux/store";
import {ANNOUNCEMENT_VIEW_ALL_ENDPOINT} from "commonUtils/globalAPISettings";
import {
  HttpException,
  ServiceAuthenticationException,
} from "services/exceptionHandlerService";
import CommonDataGrid from "components/Grids/CommonDataGrid";

const MessageListDialog = ({
  listMessageDialog,
  viewAllRecords,
  setViewAllRecords,
  showAlert,
  deletedMessage,
  setListMessageDialogOpen,
  columns,
  loader,
  setLoader,
  errorMsg,
  setErrorMsg,
  messages,
  messageYear,
  setMessageYear,
  dialogRef,
}: MessageListDialogProps) => {
  const handleYearChange = async (event?: SelectChangeEvent<number>) => {
    try {
      setMessageYear(event?.target?.value as number);
      const authState = store.getState();
      const APIHeaders = {
        Authorization: `Bearer ${authState.auth?.authDetails?.token}`,
      };
      const announcementKey = 0;
      const profileID = authState.auth?.userDetails?.profileId;
      const announcementYear = event?.target?.value;
      setLoader(true);
      const responseData = await postRequest({
        url: ANNOUNCEMENT_VIEW_ALL_ENDPOINT,
        headers: setHeader(APIHeaders),
        params: {announcementKey, profileID, announcementYear},
      });
      if (isValidObject(responseData)) {
        setViewAllRecords(responseData.announcements);
      }
      setLoader(false);
    } catch (error: any) {
      loggedMessage(error, errorMsg, PageNames.DASHBOARD);
      setLoader(false);
      handleAxiosErrors(error);
    }
  };

  const handleAxiosErrors = (error: any) => {
    const data = {pagename: PageNames.DASHBOARD};
    if (error?.code === AxiosErrorCode.ERR_BAD_REQUEST) {
      if (
        error?.response?.status === HttpStatusCode.UNAUTHORIZED ||
        error?.response?.status === HttpStatusCode.FORBIDDEN
      ) {
        ServiceAuthenticationException(error, data, error?.response?.status);
      } else if (error?.response?.status === HttpStatusCode.BAD_REQUEST) {
        setErrorMsg(error.response.data.message);
      } else if (error?.response?.status === HttpStatusCode.NOT_FOUND) {
        setErrorMsg(CPSMessages.SERVICE_NOT_FOUND);
      } else if (
        error?.response?.status === HttpStatusCode.INTERNAL_SERVER_ERROR
      ) {
        HttpException(error, data);
        setErrorMsg(CPSMessages.INTERNAL_SERVER_ERROR);
      } else {
        setErrorMsg(error.message);
      }
    } else if (error?.code === AxiosErrorCode.ECONNABORTED) {
      setErrorMsg(CPSMessages.TIMEOUT_ERROR);
    } else if (error?.code === AxiosErrorCode.ERR_NETWORK) {
      setErrorMsg(CPSMessages.NETWORK_ERROR);
    } else {
      setErrorMsg(error.message);
    }
  };

  return (
    <Dialog
      PaperProps={{
        sx: dashBoardMessageStyle.paperPropUDialog,
      }}
      open={listMessageDialog}>
      <DialogTitleCommon title="List of Messages" />
      <DialogCloseCommon
        onClick={() => {
          setListMessageDialogOpen(false);
        }}
      />
      <DialogContent ref={dialogRef}>
        {viewAllRecords && (
          <Box sx={{minHeight: "35rem"}}>
            {showAlert && (
              <Box mb={"1rem"}>
                <FormValidationAlert
                  severity={Alerts.SUCCESS}
                  message={deletedMessage}
                />
              </Box>
            )}

            <Box display={"flex"} justifyContent={"end"}>
              <FormControl
                sx={{
                  mb: 1,
                  minWidth: 80,
                }}
                size="small">
                <Select
                  sx={{height: "2rem"}}
                  value={messageYear}
                  onChange={event => {
                    handleYearChange(event);
                  }}>
                  {firstFiveYears.map((year: string, i: number) => (
                    <MenuItem className="main-font-family" key={i} value={year}>
                      {year}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <CommonDataGrid
              getRowHeight={() => "auto"}
              sx={dashBoardMessageStyle.dataGridStyle}
              getRowId={row => row?.announcementKey}
              viewAllRecords={viewAllRecords}
              columns={columns}
              hideFooter={true}
              loader={loader}
            />
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default MessageListDialog;
